import { DevicesOrderBy } from '@/__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import { GET_DEVICES } from '@features/reports2/graphql/queries/getDevices';
import { StepProps } from '@features/reports2/types';
import { ALLOWED_OPERATIONAL_STATES } from '@features/reports2/utils/allowedOperationalStates';
import { Trans } from '@lingui/macro';
import { Box } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CreateReportFieldValues } from '../../validation';
import { StepContentBox, StepFallback, StepTitle } from '../common';
import { DevicesInput } from './DevicesInput';
import { MetricsInput } from './MetricsInput';

const SuspenseComponent: FC<StepProps> = (props) => {
  const { trigger, control, watch, formState } = useFormContext<CreateReportFieldValues>();

  const { data } = useSuspenseQuery(GET_DEVICES, {
    variables: {
      input: {
        orderBy: DevicesOrderBy.NameAsc,
        operationalStates: ALLOWED_OPERATIONAL_STATES,
      },
    },
  });

  const errors = formState.errors.step1;

  useEffect(() => {
    trigger('step1');
  }, [trigger]);

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        nextStepEnabled: props.actions.nextStepEnabled && !errors?.devices && !errors?.metrics,
        onNextStep: () => {
          trigger('step1').then((success) => {
            if (success) {
              props.actions.onNextStep();
            }
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Dispositivos y métricas</Trans>}
        description={<Trans>Elige tu dispositivo y luego las métricas que deseas reportar.</Trans>}
      />
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <Controller
          control={control}
          name="step1.devices"
          render={({ field }) => (
            <DevicesInput
              data={data}
              selectedDeviceIds={field.value}
              onChangeSelectedDeviceIds={(devices) => {
                field.onChange(devices);
                trigger('step1');
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="step1.metrics"
          render={({ field }) => (
            <MetricsInput
              data={data}
              selectedDeviceIds={watch('step1.devices')}
              onChangeSelectedMetrics={(metrics) => {
                field.onChange(metrics);
                trigger('step1');
              }}
              selectedMetrics={field.value}
            />
          )}
        />
      </Box>
    </StepContentBox>
  );
};

export const DevicesAndMetricsStep: FC<StepProps> = (props) => {
  return (
    <StepFallback>
      <SuspenseComponent {...props} />
    </StepFallback>
  );
};
